import React, { useCallback, useEffect, useRef, useState } from 'react';
import classes from './ChatBox.module.css';
import { Chats } from './Chats/Chats';

const ChatGlyph = () => (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" aria-hidden="true">
        <path
            d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5Z"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const ChevronGlyph = () => (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" aria-hidden="true">
        <path d="m6 9 6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

export const ChatBox = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);

    // The socket listener inside <Chats> is registered once, so it keeps hold of
    // whatever callback it saw first. Read "is the panel open?" from a ref so the
    // unread counter reacts to the *current* state instead of the mount-time one.
    const isOpenRef = useRef(isOpen);
    isOpenRef.current = isOpen;

    const notifyIncomingMessage = useCallback(() => {
        if (!isOpenRef.current) {
            setUnreadCount((count) => count + 1);
        }
    }, []);

    const openChat = useCallback(() => {
        setIsOpen(true);
        setUnreadCount(0);
    }, []);

    const closeChat = useCallback(() => setIsOpen(false), []);

    const toggleChat = useCallback(() => {
        if (isOpenRef.current) {
            closeChat();
        } else {
            openChat();
        }
    }, [closeChat, openChat]);

    // Escape closes the panel, the way every other overlay in the app behaves.
    useEffect(() => {
        if (!isOpen) return undefined;
        const onKeyDown = (event) => {
            if (event.key === 'Escape') closeChat();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [isOpen, closeChat]);

    const badgeLabel = unreadCount > 9 ? '9+' : `${unreadCount}`;
    const hasUnread = unreadCount > 0 && !isOpen;

    return (
        <div className={classes.root}>
            <section
                className={`${classes.panel} ${isOpen ? classes.panelOpen : ''}`}
                role="dialog"
                aria-label="Presentation comments"
                aria-hidden={!isOpen}
            >
                <header className={classes.header}>
                    <span className={classes.headerIcon} aria-hidden="true">
                        <ChatGlyph />
                    </span>
                    <div className={classes.headerText}>
                        <span className={classes.headerTitle}>Comments</span>
                        <span className={classes.headerSubtitle}>
                            <i className={classes.liveDot} aria-hidden="true" />
                            Live with everyone here
                        </span>
                    </div>
                    <button
                        type="button"
                        className={classes.headerClose}
                        onClick={closeChat}
                        aria-label="Close comments"
                        title="Close comments"
                    >
                        <ChevronGlyph />
                    </button>
                </header>

                <Chats isOpen={isOpen} onIncomingMessage={notifyIncomingMessage} />
            </section>

            <button
                type="button"
                className={classes.launcher}
                onClick={toggleChat}
                aria-label={isOpen ? 'Close comments' : 'Open comments'}
                aria-expanded={isOpen}
                title={isOpen ? 'Close comments' : 'Open comments'}
            >
                {/* Keyed so React remounts the span and replays the swap animation. */}
                <span key={isOpen ? 'open' : 'closed'} className={classes.launcherGlyph} aria-hidden="true">
                    {isOpen ? <ChevronGlyph /> : <ChatGlyph />}
                </span>
                {hasUnread && (
                    <span className={classes.unreadBadge} aria-live="polite">
                        {badgeLabel}
                        <span className={classes.srOnly}> unread messages</span>
                    </span>
                )}
                {hasUnread && <span className={classes.pulseRing} aria-hidden="true" />}
            </button>
        </div>
    );
};
